export const MISSION_AUDIO_TOPIC = "__mission_audio__";

export const MISSION_AUDIO_ACTION_TYPE = "mission_audio_play";

export const MISSION_AUDIO_RULE_ID = "mission-audio:configured";

export const MISSION_AUDIO_INTERVAL_DEFAULT = 30;
export const MISSION_AUDIO_VOLUME_DEFAULT = 80;
export const MISSION_AUDIO_INTERVAL_MIN = 5;
export const MISSION_AUDIO_INTERVAL_MAX = 3600;

export type BehaviourMissionAudio = {
    track_id?: string;
    interval_sec?: number | string;
    volume?: number | string;
    name?: string;
    enabled?: boolean;
};

export const isMissionAudioSource = (source: { kind?: string } | undefined) =>
    source?.kind === "mission_audio";

export const isMissionAudioCondition = (condition: { topic?: string } | undefined) =>
    condition?.topic === MISSION_AUDIO_TOPIC;

export const isMissionAudioRule = (rule: {
    id?: string;
    isMissionAudio?: boolean;
    conditions?: { items?: Array<{ topic?: string }> };
} | undefined) =>
    Boolean(
        rule?.isMissionAudio ||
            String(rule?.id || "") === MISSION_AUDIO_RULE_ID ||
            isMissionAudioCondition(rule?.conditions?.items?.[0]),
    );

export const missionAudioToRule = (missionAudio: BehaviourMissionAudio | undefined) => {
    const trackId = String(missionAudio?.track_id || "").trim();
    if (!trackId) return null;

    const intervalSec = Number(missionAudio?.interval_sec);
    const volume = Number(missionAudio?.volume);
    const enabled = missionAudio?.enabled !== false;
    const storedName =
        typeof missionAudio?.name === "string" ? missionAudio.name.trim() : "";

    return {
        id: MISSION_AUDIO_RULE_ID,
        name: storedName || "Audio during mission",
        enabled,
        isMissionAudio: true,
        conditions: {
            logic: "and",
            items: [
                {
                    source: "mission_audio",
                    topic: MISSION_AUDIO_TOPIC,
                    field: "mission_running",
                    operator: "eq",
                    value: "true",
                },
            ],
        },
        action: {
            type: MISSION_AUDIO_ACTION_TYPE,
            params: {
                track_id: trackId,
                interval_sec: String(
                    Number.isFinite(intervalSec) && intervalSec > 0
                        ? intervalSec
                        : MISSION_AUDIO_INTERVAL_DEFAULT,
                ),
                volume: String(
                    Number.isFinite(volume) ? volume : MISSION_AUDIO_VOLUME_DEFAULT,
                ),
                track_name: storedName,
            },
        },
    };
};

export const buildMissionAudioRules = (
    missionAudio: BehaviourMissionAudio | undefined,
) => {
    const rule = missionAudioToRule(missionAudio);
    return rule ? [rule] : [];
};

export const missionAudioFromRule = (
    rule: {
        name?: string;
        enabled?: boolean;
        action?: { params?: Record<string, string> };
    },
    options: { enabled?: boolean } = {},
): BehaviourMissionAudio => {
    const params = rule.action?.params ?? {};
    const trackId = String(params.track_id || "").trim();
    const intervalSec = Number(params.interval_sec);
    const volume = Number(params.volume);
    const name =
        (typeof rule.name === "string" && rule.name.trim()) ||
        String(params.track_name || "").trim() ||
        undefined;
    const next: BehaviourMissionAudio = {
        track_id: trackId,
        interval_sec: Number.isFinite(intervalSec)
            ? intervalSec
            : MISSION_AUDIO_INTERVAL_DEFAULT,
        volume: Number.isFinite(volume) ? volume : MISSION_AUDIO_VOLUME_DEFAULT,
    };
    if (name) next.name = name;
    const enabled = options.enabled !== undefined ? options.enabled : rule.enabled !== false;
    if (!enabled) next.enabled = false;
    return next;
};
